import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Lock, CheckCircle, Clock, ChevronRight, MapPin, Zap, Calendar, AlertCircle } from 'lucide-react';

const RecipientEventView = ({ token }) => {
  const [event, setEvent] = useState(null);
  const [stations, setStations] = useState([]);
  const [stationMessages, setStationMessages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedStation, setExpandedStation] = useState(null);
  const [confirmedStations, setConfirmedStations] = useState({}); // Track confirmed stations
  const [confirmingStation, setConfirmingStation] = useState(null);
  const [confirmData, setConfirmData] = useState({
    confirmedTime: '',
    confirmedLocation: ''
  });

  useEffect(() => {
    loadEvent();
  }, [token]);

  const loadEvent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `http://localhost:4500/api/events/view/${token}`
      );
      setEvent(response.data.event);
      console.log('✅ Event loaded:', response.data.event);
      if (response.data.event?.id) {
        loadStations(response.data.event.id);
      }
    } catch (error) {
      console.error('❌ Error loading event:', error);
      setError('Event nicht gefunden oder ungültiger Link');
    } finally {
      setLoading(false);
    }
  };

  const loadStations = async (eventId) => {
    try {
      const response = await axios.get(
        `http://localhost:4500/api/events/${eventId}/stations`
      );
      setStations(response.data.stations || []);
      console.log('✅ Stations loaded:', response.data.stations);
      response.data.stations?.forEach(station => {
        loadStationMessages(station.id);
      });
    } catch (error) {
      console.error('❌ Error loading stations:', error);
    }
  };

  const loadStationMessages = async (stationId) => {
    try {
      const response = await axios.get(
        `http://localhost:4500/api/stations/${stationId}/messages`
      );
      setStationMessages(prev => ({
        ...prev,
        [stationId]: response.data.messages || []
      }));
    } catch (error) {
      console.error(`❌ Error loading messages for station ${stationId}:`, error);
    }
  };

  const isStationReleased = (station) => {
    const now = new Date();
    const releaseDateTime = new Date(`${station.scheduled_date}T${station.scheduled_time}`);
    return now >= releaseDateTime;
  };

  const getCountdown = (station) => {
    const now = new Date();
    const releaseDateTime = new Date(`${station.scheduled_date}T${station.scheduled_time}`);
    const diff = releaseDateTime - now;
    
    if (diff < 0) return null;
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `In ${days} Tag${days !== 1 ? 'en' : ''} freigeschalten`;
    if (hours > 0) return `In ${hours} Stunde${hours !== 1 ? 'n' : ''} freigeschalten`;
    return 'Gleich freigeschalten!';
  };

  const handleConfirmStation = async (e) => {
    e.preventDefault();
    if (!confirmingStation || !confirmData.confirmedTime || !confirmData.confirmedLocation) {
      alert('Bitte fülle alle Felder aus');
      return;
    }

    try {
      const response = await axios.post(
        `http://localhost:4500/api/stations/${confirmingStation.id}/confirm`,
        {
          eventQrToken: token,
          confirmedTime: confirmData.confirmedTime,
          confirmedLocation: confirmData.confirmedLocation
        }
      );
      console.log('✅ Station confirmed:', response.data);
      // Mark this station as confirmed
      setConfirmedStations(prev => ({
        ...prev,
        [confirmingStation.id]: true
      }));
      setExpandedStation(confirmingStation.id);
      setConfirmingStation(null);
      setConfirmData({ confirmedTime: '', confirmedLocation: '' });
    } catch (error) {
      console.error('❌ Error confirming station:', error);
      alert('Fehler beim Bestätigen der Station');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin mb-4">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full"></div>
          </div>
          <p className="text-slate-300">⏳ Schnitzeljagd wird geladen...</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 backdrop-blur-md border border-red-500/30 rounded-2xl p-12 text-center max-w-md shadow-xl">
          <p className="text-red-400 text-2xl font-bold mb-4">❌ Event nicht gefunden</p>
          <p className="text-slate-400 mb-6">Bitte überprüfe den QR-Code oder NFC-Chip</p>
          <p className="text-slate-500 text-sm">Wenn das Problem weiterhin besteht, kontaktiere TimeLock Support</p>
        </div>
      </div>
    );
  }

  const unlockedCount = stations.filter(s => isStationReleased(s)).length;
  const progressPercent = stations.length > 0 ? Math.round((unlockedCount / stations.length) * 100) : 0;

  const getCardColor = (index) => {
    const colors = [
      'from-purple-600 to-purple-700',
      'from-blue-600 to-blue-700',
      'from-pink-600 to-pink-700',
      'from-orange-600 to-orange-700',
      'from-teal-600 to-teal-700',
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Hero Header */}
        <div className="bg-gradient-to-br from-purple-600 via-purple-500 to-pink-600 rounded-3xl p-8 md:p-12 text-white mb-12 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>
          
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <MapPin size={24} />
              <h1 className="text-5xl md:text-6xl font-black">{event.name}</h1>
            </div>
            
            {event.recipient_name && (
              <p className="text-xl md:text-2xl text-white/90 mb-6 font-semibold">
                💜 Speziell für <span className="text-white">{event.recipient_name}</span> erstellt
              </p>
            )}
            
            {event.description && (
              <p className="text-lg text-white/80 mb-8">{event.description}</p>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3">
                <p className="text-sm text-white/70">Stationen freigeschalten</p>
                <p className="text-3xl font-bold">{unlockedCount}/{stations.length}</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3">
                <p className="text-sm text-white/70">Fortschritt</p>
                <p className="text-3xl font-bold">{progressPercent}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-slate-800/50 backdrop-blur-md border border-purple-500/30 rounded-2xl p-6 mb-12 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Zap size={20} className="text-purple-400" />
              <span className="text-white font-bold">Dein Fortschritt</span>
            </div>
            <span className="text-purple-300 font-bold text-lg">{progressPercent}%</span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-4 overflow-hidden">
            <div
              className={`bg-gradient-to-r ${getCardColor(0)} h-4 rounded-full transition-all duration-500`}
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
          <p className="text-slate-400 text-sm mt-4">
            {unlockedCount === 0 && '👀 Keine Station ist noch freigeschalten - bald ist es soweit!'}
            {unlockedCount > 0 && unlockedCount < stations.length && `🎯 Du hast ${unlockedCount} von ${stations.length} Stationen entdeckt!`}
            {unlockedCount === stations.length && stations.length > 0 && '🎉 Du hast alle Stationen entdeckt! Herzlichen Glückwunsch!'}
            {stations.length === 0 && '⏳ Diese Schnitzeljagd hat noch keine Stationen...'}
          </p>
        </div>

        {/* Stations Grid */}
        <div className="space-y-6">
          {stations.length === 0 ? (
            <div className="bg-slate-800/50 backdrop-blur-md border-2 border-dashed border-purple-500/30 rounded-2xl p-12 text-center">
              <Calendar size={48} className="mx-auto mb-4 text-slate-400" />
              <p className="text-slate-300 text-lg mb-2">Diese Schnitzeljagd hat noch keine Stationen</p>
              <p className="text-slate-500">Der Ersteller wird bald spannende Inhalte hinzufügen...</p>
            </div>
          ) : (
            stations.map((station, index) => {
              const isReleased = isStationReleased(station);
              const countdown = getCountdown(station);
              const isExpanded = expandedStation === station.id;
              const messages = stationMessages[station.id] || [];

              return (
                <div key={station.id}>
                  <div
                    className={`rounded-2xl overflow-hidden transition-all transform cursor-pointer ${
                      isReleased
                        ? `bg-gradient-to-br ${getCardColor(index)} border border-slate-600/50 hover:shadow-2xl hover:scale-102`
                        : 'bg-slate-800/30 border border-slate-700/50'
                    }`}
                    onClick={() => {
                      if (isReleased) {
                        setExpandedStation(isExpanded ? null : station.id);
                        if (!isExpanded) {
                          setConfirmingStation(station);
                        } else {
                          setConfirmingStation(null);
                        }
                      }
                    }}
                  >
                    <div className={`p-6 ${isReleased ? 'text-white' : 'text-slate-400'}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`p-3 rounded-lg ${isReleased ? 'bg-white/20' : 'bg-slate-700/50'}`}>
                            {isReleased ? (
                              <CheckCircle size={24} className="text-white" />
                            ) : (
                              <Lock size={24} className={isReleased ? 'text-white' : 'text-slate-500'} />
                            )}
                          </div>
                          <div className="flex-1">
                            <h3 className={`text-2xl font-bold mb-2 ${isReleased ? 'text-white' : 'text-slate-400'}`}>
                              {station.title}
                            </h3>
                            {station.description && (
                              <p className={`text-sm mb-3 ${isReleased ? 'text-white/80' : 'text-slate-500'}`}>
                                {station.description}
                              </p>
                            )}
                            <div className="flex items-center gap-4 text-sm flex-wrap">
                              <div className={`flex items-center gap-1 ${isReleased ? 'bg-white/20' : 'bg-slate-700/50'} px-3 py-1 rounded-full`}>
                                <Calendar size={14} />
                                {station.scheduled_date} um {station.scheduled_time}
                              </div>
                              {station.location && (
                                <div className={`flex items-center gap-1 ${isReleased ? 'bg-white/20' : 'bg-slate-700/50'} px-3 py-1 rounded-full`}>
                                  <MapPin size={14} />
                                  {station.location}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          {!isReleased && countdown && (
                            <p className="text-sm font-semibold text-slate-300">{countdown}</p>
                          )}
                          {isReleased && (
                            <ChevronRight size={24} className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          )}
                        </div>
                      </div>

                      {isReleased && station.initial_message && !confirmedStations[station.id] && (
                        <div className={`mt-4 p-4 rounded-lg ${isReleased ? 'bg-white/20' : 'bg-slate-700/50'}`}>
                          <p className={`text-sm font-semibold mb-2 ${isReleased ? 'text-white/80' : 'text-slate-500'}`}>
                            🔒 Nachrichten sind versteckt
                          </p>
                          <p className={`text-xs ${isReleased ? 'text-white/70' : 'text-slate-400'}`}>
                            Bestätige deine Identität um die Nachrichten zu sehen
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {isExpanded && isReleased && (
                    <div className="bg-slate-800/50 backdrop-blur-md border border-slate-700 rounded-2xl p-6 mt-4 space-y-4">
                      {/* Confirmation Section - ALWAYS show first if not confirmed */}
                      {!confirmedStations[station.id] && (
                        <div className="bg-blue-600/20 border border-blue-500/30 rounded-lg p-4 space-y-3">
                          <h4 className="font-bold text-white flex items-center gap-2">
                            <AlertCircle size={18} />
                            🔐 Identitätsbestätigung erforderlich
                          </h4>
                          <p className="text-sm text-slate-300">
                            Um die versteckten Nachrichten zu sehen, bestätige bitte, dass du gerade an diesem Ort und zur richtigen Uhrzeit bist:
                          </p>
                          <form onSubmit={handleConfirmStation} className="space-y-3">
                            <div>
                              <label className="block text-sm text-slate-400 mb-1">📍 Ort (wo bist du gerade?):</label>
                              <input
                                type="text"
                                value={confirmData.confirmedLocation}
                                onChange={(e) => setConfirmData({ ...confirmData, confirmedLocation: e.target.value })}
                                placeholder={`z.B. ${station.location || 'Ort eingeben'}`}
                                className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-slate-400 mb-1">🕐 Uhrzeit (aktuell):</label>
                              <input
                                type="time"
                                value={confirmData.confirmedTime}
                                onChange={(e) => setConfirmData({ ...confirmData, confirmedTime: e.target.value })}
                                className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                              />
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="submit"
                                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 px-4 py-3 rounded font-semibold transition-all text-white"
                              >
                                ✓ Bestätigen & Nachrichten anzeigen
                              </button>
                            </div>
                          </form>
                        </div>
                      )}

                      {/* Show content ONLY after confirmation */}
                      {confirmedStations[station.id] && (
                        <>
                          {station.instructions && (
                            <div>
                              <h4 className="font-bold text-white mb-2">📋 Anleitung</h4>
                              <p className="text-slate-300 whitespace-pre-wrap">{station.instructions}</p>
                            </div>
                          )}

                          {station.initial_message && (
                            <div className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 border border-purple-500/30 rounded-lg p-4">
                              <h4 className="font-bold text-white mb-2">💌 Nachricht</h4>
                              <p className="text-slate-200 whitespace-pre-wrap">{station.initial_message}</p>
                            </div>
                          )}

                          {messages.length > 0 && (
                            <div className="mt-6">
                              <h4 className="font-bold text-white mb-4">📨 Nachrichten dieser Station ({messages.length})</h4>
                              <div className="space-y-3">
                                {messages.map((msg, msgIdx) => (
                                  <div key={msg.id} className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
                                    <h5 className="font-semibold text-purple-300 mb-2">
                                      Nachricht #{msgIdx + 1} {msg.recipient_name && `(für ${msg.recipient_name})`}
                                    </h5>
                                    <p className="text-slate-300 whitespace-pre-wrap">{msg.message_text}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <button
                            onClick={() => setConfirmedStations(prev => ({ ...prev, [station.id]: false }))}
                            className="w-full mt-4 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2 px-4 rounded-lg transition-all text-sm"
                          >
                            🔒 Nachrichten erneut verstecken
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="mt-16 text-center text-slate-500 text-sm">
          <p>💜 TimeLock - Zeitkapseln für digitales Erbe</p>
          <p>© 2024 TimeLock. Alle Rechte vorbehalten.</p>
        </div>
      </div>
    </div>
  );
};

export default RecipientEventView;
